import React, { useState, useEffect } from "react";
import { Avatar, Button, Menu, MenuItem } from "@mui/material";
import axios from "axios";
import moment from "moment";
import myImg from "../images/img1.jpeg";

const CommentSection = ({ projectId }) => {
  const apiUrl =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_API_URL_DEV
      : process.env.REACT_APP_API_URL_PROD;

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [editCommentId, setEditCommentId] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [replyAnchorEl, setReplyAnchorEl] = useState({});
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [commenterIsNew, setCommenterIsNew] = useState(true);

  const [replies, setReplies] = useState({});
  const [newReply, setNewReply] = useState("");
  const [editReplyId, setEditReplyId] = useState(null);
  const [activeReplyCommentId, setActiveReplyCommentId] = useState(null);

  const [loadingComment, setLoadingComment] = useState(true);

  useEffect(() => {
    const storedName = localStorage.getItem("commenterName");
    const storedEmail = localStorage.getItem("commenterEmail");
    if (storedName) setName(storedName);
    if (storedEmail) setEmail(storedEmail);

    if (projectId) {
      axios
        .get(`${apiUrl}/comments`, { params: { project_id: projectId } })
        .then((response) => {
          setComments(response.data);
          setLoadingComment(false);
        })
        .catch((error) => {
          console.error("Error fetching comments:", error);
          setLoadingComment(false);
        });
    }
  }, [apiUrl, projectId]);

  useEffect(() => {
    const storedName = localStorage.getItem("commenterName");
    if (storedName) setCommenterIsNew(false);
  });

  const handleAddComment = () => {
    if (newComment.trim() !== "") {
      const commentDetails = {
        comment_text: newComment,
        commenter_name: name.trim() ? name : "Anonymous",
        commenter_email: email.trim(),
        project_id: projectId,
        comment_date: new Date().toISOString(),
      };

      if (editCommentId) {
        axios
          .put(`${apiUrl}/comments/${editCommentId}`, commentDetails)
          .then(() => {
            setComments(
              comments.map((comment) =>
                comment.id === editCommentId
                  ? { ...comment, ...commentDetails }
                  : comment
              )
            );
            setEditCommentId(null);
            setNewComment("");
          })
          .catch((error) => {
            console.error("Error editing comment:", error);
          });
      } else {
        axios
          .post(`${apiUrl}/comments`, commentDetails)
          .then((res) => {
            setComments([{ ...commentDetails, id: res.data.id }, ...comments]);
            setNewComment("");
          })
          .catch((error) => {
            console.error("Error posting comment:", error);
          });
      }

      if (name.trim()) {
        localStorage.setItem("commenterName", name.trim());
      }
      if (email.trim()) {
        localStorage.setItem("commenterEmail", email.trim());
      }
    }
  };

  const handleEditComment = (comment) => {
    const res = window.confirm("You want t edit " + comment.id);
    if (res) {
      setEditCommentId(comment.id);
      setNewComment(comment.comment_text);
    }
  };

  const handleCancelEdit = () => {
    setEditCommentId(null);
    setNewComment("");
  };

  const handleDeleteComment = (id) => {
    const res = window.confirm("Are you sure you want to delete " + id);
    if (res) {
      axios
        .delete(`${apiUrl}/comments/${id}`)
        .then(() => {
          setComments(comments.filter((comment) => comment.id !== id));
        })
        .catch((error) => {
          console.error("Error deleting comment:", error);
        });
    }
  };

  const handleCopyComment = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert("Comment copied to clipboard!");
      })
      .catch((error) => {
        console.error("Error copying comment:", error);
      });
  };

  const handleOpenCommentMenu = (event, commentId) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveCommentId(commentId);
  };

  const handleCloseCommentMenu = () => {
    setMenuAnchorEl(null);
    setActiveCommentId(null);
  };

  useEffect(() => {
    const initialReplyMenuAnchorEl = {};
    comments.forEach((comment) => {
      if (replies[comment.id]) {
        replies[comment.id].forEach((reply) => {
          initialReplyMenuAnchorEl[reply.id] = null;
        });
      }
    });
    setReplyAnchorEl(initialReplyMenuAnchorEl);
  }, [comments, replies]);

  const handleOpenCommentReplyMenu = (event, replyId) => {
    setReplyAnchorEl((prevAnchorEl) => ({
      ...prevAnchorEl,
      [replyId]: event.currentTarget,
    }));
  };

  const handleCloseCommentReplyMenu = (replyId) => {
    setReplyAnchorEl((prevAnchorEl) => ({
      ...prevAnchorEl,
      [replyId]: null,
    }));
  };

  useEffect(() => {
    if (projectId) {
      axios
        .get(`${apiUrl}/comments`, { params: { project_id: projectId } })
        .then((response) => {
          setComments(response.data);
          setLoadingComment(false);
          // Fetch replies for each comment
          response.data.forEach((comment) => {
            axios
              .get(`${apiUrl}/replies`, { params: { comment_id: comment.id } })
              .then((replyResponse) => {
                setReplies((prevReplies) => ({
                  ...prevReplies,
                  [comment.id]: replyResponse.data,
                }));
              });
          });
        })
        .catch((error) => {
          console.error("Error fetching comments:", error);
          setLoadingComment(false);
        });
    }
  }, [apiUrl, projectId]);

  const handleAddReply = (commentId) => {
    if (newReply.trim() !== "") {
      const replyDetails = {
        comment_id: commentId,
        reply_text: newReply,
      };

      if (editReplyId) {
        axios
          .put(`${apiUrl}/replies/${editReplyId}`, replyDetails)
          .then(() => {
            setReplies((prevReplies) => ({
              ...prevReplies,
              [commentId]: prevReplies[commentId].map((reply) =>
                reply.id === editReplyId ? { ...reply, ...replyDetails } : reply
              ),
            }));
            setEditReplyId(null);
            setNewReply("");
          })
          .catch((error) => {
            console.error("Error editing reply:", error);
          });
      } else {
        axios
          .post(`${apiUrl}/replies`, replyDetails)
          .then((res) => {
            setReplies((prevReplies) => ({
              ...prevReplies,
              [commentId]: [
                ...(prevReplies[commentId] || []),
                { ...replyDetails, id: res.data.id },
              ],
            }));
            setNewReply("");
          })
          .catch((error) => {
            console.error("Error posting reply:", error);
          });
      }
    }
  };

  const handleEditReply = (reply, commentId) => {
    setEditReplyId(reply.id);
    setActiveReplyCommentId(commentId);
    setNewReply(reply.reply_text);
  };

  const handleDeleteReply = (commentId, replyId) => {
    axios
      .delete(`${apiUrl}/replies/${replyId}`)
      .then(() => {
        setReplies((prevReplies) => ({
          ...prevReplies,
          [commentId]: prevReplies[commentId].filter(
            (reply) => reply.id !== replyId
          ),
        }));
      })
      .catch((error) => {
        console.error("Error deleting reply:", error);
      });
  };

  return (
    <div className="comment-section">
      
      {/* <h6>Discussions</h6> */}

      <div className="comment-section">
        {loadingComment ? (
          <div className="p-5 text-center">
            <p>Loading comments...</p>
          </div>
        ) : (
          <div className="comments">
            {comments
              .filter((comment) => comment.project_id === projectId)
              .map((comment, index) => (
                <div key={index} className={`comment px-1 py-3 `}>
                  <div className="comment-body">
                    <div className="d-flex">
                      <Avatar sx={{ width: 24, height: 24 }} />
                      <p className="ms-3">{comment.comment_text}</p>
                    </div>
                    <div className="d-flex justify-content-between">
                      <div style={{ marginLeft: "56px" }}>
                        <b>{comment.commenter_name}</b>
                        <b style={{ display: "block", fontSize: "12px" }}>
                          {moment(comment.comment_date).format(
                            "D MMMM YYYY | h:mma"
                          )}
                        </b>
                      </div>
                      <div
                        className="mt-2"
                        style={{ position: "absolute", right: "70px" }}
                      >
                        <button
                          className="btn bi bi-three-dots-vertical position-absolute"
                          id={`basic-button-${comment.id}`}
                          aria-controls={
                            menuAnchorEl
                              ? `basic-menu-${comment.id}`
                              : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={menuAnchorEl ? "true" : undefined}
                          onClick={(event) =>
                            handleOpenCommentMenu(event, comment.id)
                          }
                        ></button>
                      </div>
                    </div>

                    
                    <Menu
                      id={`basic-menu-${comment.id}`}
                      anchorEl={menuAnchorEl}
                      open={activeCommentId === comment.id}
                      onClose={handleCloseCommentMenu}
                      MenuListProps={{
                        "aria-labelledby": `basic-button-${comment.id}`,
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          setActiveReplyCommentId(comment.id);
                          handleCloseCommentMenu();
                        }}
                      >
                        <i className="bi bi-arrow-up-left-circle me-3"></i>{" "}
                        Reply Discussion
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleEditComment(comment);
                          handleCloseCommentMenu();
                        }}
                      >
                        <i className="bi bi-pen me-3"></i> Edit Discussion
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleCopyComment(comment.comment_text);
                          handleCloseCommentMenu();
                        }}
                      >
                        <i className="bi bi-file-check me-3"></i> Copy
                        Discussion
                      </MenuItem>
                      <MenuItem
                        className="text-danger"
                        onClick={() => {
                          handleDeleteComment(comment.id);
                          handleCloseCommentMenu();
                        }}
                      >
                        <i className="bi bi-trash me-3"></i> Delete Discussion
                      </MenuItem>
                    </Menu>
                  </div>

                  {replies[comment.id] &&
                    replies[comment.id].map((reply, replyIndex) => (
                      <div
                        key={replyIndex}
                        className="replies mt-4 p-3 rounded-4"
                      >
                        <div className="d-flex">
                          <Avatar
                            src={myImg}
                            sx={{
                              width: 24,
                              height: 24,
                              border: "2px solid grey",
                            }}
                          />
                          <p className="ms-3">{reply.reply_text}</p>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div style={{ marginLeft: "56px" }}>
                            <b>Promise (Site owner)</b>
                            <b style={{ display: "block", fontSize: "12px" }}>
                              {moment(reply.reply_date).format(
                                "D MMMM YYYY | h:mma"
                              )}
                            </b>
                          </div>
                          <div
                            className="mt-2"
                            style={{ position: "absolute", right: "70px" }}
                          >
                            <button
                              className="btn bi bi-three-dots-vertical position-absolute"
                              id={`basic-button-${reply.id}`}
                              aria-controls={
                                replyAnchorEl
                                  ? `basic-menu-${reply.id}`
                                  : undefined
                              }
                              aria-haspopup="true"
                              aria-expanded={replyAnchorEl ? "true" : undefined}
                              onClick={(event) =>
                                handleOpenCommentReplyMenu(event, reply.id)
                              }
                            ></button>
                          </div>
                        </div>
                        <Menu
                          id={`basic-menu-${reply.id}`}
                          anchorEl={replyAnchorEl[reply.id]}
                          open={Boolean(replyAnchorEl[reply.id])}
                          onClose={() => handleCloseCommentReplyMenu(reply.id)}
                          MenuListProps={{
                            "aria-labelledby": `basic-button-${reply.id}`,
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              handleEditReply(reply, comment.id);
                              handleCloseCommentReplyMenu(reply.id);
                            }}
                          >
                            <i className="bi bi-pen me-3"></i> Edit Reply
                          </MenuItem>

                          <MenuItem
                            className="text-danger"
                            onClick={() => {
                              handleDeleteReply(comment.id, reply.id);
                              handleCloseCommentReplyMenu();
                            }}
                          >
                            <i className="bi bi-trash me-3"></i> Delete Reply
                          </MenuItem>
                        </Menu>
                      </div>
                    ))}

                  {activeReplyCommentId === comment.id && (
                    <div className="add-reply">
                      <textarea
                        className="form-control my-4"
                        value={newReply}
                        onChange={(e) => setNewReply(e.target.value)}
                        placeholder={
                          "Replying to " + comment.commenter_name + "..."
                        }
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          handleAddReply(comment.id);
                          setActiveReplyCommentId(null);
                        }}
                        style={{ padding: "8px 16px" }}
                      >
                        {editReplyId ? "Update Reply" : "Add Reply"}
                      </Button>
                      {(editReplyId || activeReplyCommentId) && (
                        <Button
                          className="ms-3"
                          variant="contained"
                          color="error"
                          onClick={() => {
                            setEditReplyId(null);
                            setActiveReplyCommentId(null);
                            setNewReply(""); // Clear the textarea
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="add-comment">
        {commenterIsNew && (
          <div className="commenter-info">
            <input
              className="form-control"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
            />
            <input
              className="form-control"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email (optional)"
              style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
            />
          </div>
        )}

        {!activeReplyCommentId && (
          <div>
            <textarea
              className="form-control"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
            />
            <Button
              variant="outlined"
              onClick={handleAddComment}
              style={{ padding: "8px 16px" }}
            >
              {editCommentId ? "Update Comment" : "Add Comment"}
            </Button>
            {editCommentId && (
              <Button
                className="ms-3"
                variant="contained"
                color="error"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>

      {comments.length === 0 && (
        <div className="text-center mt-5">
          <p>No comment yet</p>
        </div>
      )}
    </div>
  );
};

export default CommentSection;
